/**
 * Reglas puras para decidir si la app puede operar offline tras bootstrap incompleto en red.
 * business_profile (Session Context) es obligatorio fuera de onboarding.
 *
 * `resolveOperationalShellGate` combina estas reglas con onboarding / invitación /
 * ready flags. El compositor (`App.tsx`) decide la pantalla; este módulo no renderiza.
 */

export type OperationalShellGateInput = {
  session: boolean;
  accountSetupResolved: boolean;
  requiresOwnerOnboarding: boolean | null;
  pendingEmployeeInvitation: { isOwner: boolean } | null;
  /** El usuario eligió esperar una invitación de empleado en vez de armar negocio. */
  awaitingEmployeeInvitation: boolean;
  hasOrgAndBranches: boolean;
  hasBusinessProfile: boolean;
  offlineBootstrapEvidence: boolean | null;
  initialAuthReady: boolean;
  initialBranchSelectionReady: boolean;
  employeeAccessReady: boolean;
};

export type OperationalShellGate = {
  needsOnboarding: boolean;
  needsPendingInvitation: boolean;
  needsAwaitingInvitation: boolean;
  needsOfflineSetupHint: boolean;
  accountOperational: boolean;
  accountOperationalIncludingOffline: boolean;
  bootstrapping: boolean;
};

/**
 * B0-04 — la entrada es siempre online. `accountOperational` ya cubre el único
 * atajo local válido: un device que volvió con `requiresOwnerOnboarding === false`
 * (o con org y sucursales) primado desde el snapshot. La evidencia suelta
 * (ventas/catálogo en SQLite) no alcanza para armar una cuenta nueva.
 */
export function canEnterOperationalApp(params: {
  accountOperational: boolean;
  hasBusinessProfile: boolean;
}): boolean {
  if (!params.hasBusinessProfile) return false;
  return params.accountOperational;
}

/** Esperar evaluación de evidencia local antes de mostrar OfflineSetupRequired. */
export function shouldWaitOfflineBootstrapEvidence(params: {
  session: boolean;
  needsOnboarding: boolean;
  requiresOwnerOnboarding: boolean | null;
  offlineBootstrapEvidence: boolean | null;
}): boolean {
  return (
    params.session &&
    !params.needsOnboarding &&
    params.requiresOwnerOnboarding === null &&
    params.offlineBootstrapEvidence === null
  );
}

export function shouldShowOfflineSetupRequired(params: {
  session: boolean;
  accountSetupResolved: boolean;
  needsOnboarding: boolean;
  requiresOwnerOnboarding: boolean | null;
  offlineBootstrapEvidence: boolean | null;
  hasBusinessProfile: boolean;
}): boolean {
  if (!params.session || !params.accountSetupResolved || params.needsOnboarding) {
    return false;
  }
  if (!params.hasBusinessProfile) {
    // Cuenta ya operativa online: falta rubro → reintentar (no esperar evidencia offline).
    if (params.requiresOwnerOnboarding === false) return true;
    // Setup remoto desconocido: solo bloquear cuando la evidencia local ya se evaluó.
    return params.offlineBootstrapEvidence !== null;
  }
  if (params.requiresOwnerOnboarding !== null) {
    return false;
  }
  // B0-04: setup remoto desconocido → OfflineSetup en cuanto se evaluó lo local,
  // aunque haya evidencia. Mejor pedir red que dejar el spinner girando.
  return params.offlineBootstrapEvidence !== null;
}

/**
 * Orquesta el gate de arranque. `needsOnboarding` / invitación de empleado son
 * estados terminales: no deben activar OfflineSetup ni el spinner de
 * `waitingOperationalGate`, o `refreshAccountSetup` entra en loop.
 *
 * `canEnterOperationalApp` no sabe de onboarding — el compositor tiene que
 * evaluar `needsOnboarding` / `needsPendingInvitation` antes de montar AppNavigator.
 */
export function resolveOperationalShellGate(
  input: OperationalShellGateInput,
): OperationalShellGate {
  const accountOperational =
    input.requiresOwnerOnboarding === false || input.hasOrgAndBranches;

  const accountOperationalIncludingOffline = canEnterOperationalApp({
    accountOperational,
    hasBusinessProfile: input.hasBusinessProfile,
  });

  const needsPendingInvitation =
    input.session &&
    input.pendingEmployeeInvitation != null &&
    !input.hasOrgAndBranches &&
    !input.pendingEmployeeInvitation.isOwner;

  /**
   * Espera invitación: el servidor ya manda `requires_owner_onboarding = false`,
   * pero el chequeo local evita empujar el wizard si el bootstrap llega viejo.
   */
  const needsAwaitingInvitation =
    input.session &&
    input.awaitingEmployeeInvitation &&
    !input.hasOrgAndBranches &&
    !needsPendingInvitation;

  const needsOnboarding =
    input.session &&
    input.requiresOwnerOnboarding === true &&
    !input.hasOrgAndBranches &&
    !input.awaitingEmployeeInvitation;

  /** Pantallas terminales: no disparan OfflineSetup ni el spinner del gate. */
  const needsEntryScreen = needsOnboarding || needsPendingInvitation || needsAwaitingInvitation;

  const waitingOfflineBootstrapEvidence = shouldWaitOfflineBootstrapEvidence({
    session: input.session,
    needsOnboarding: needsEntryScreen,
    requiresOwnerOnboarding: input.requiresOwnerOnboarding,
    offlineBootstrapEvidence: input.offlineBootstrapEvidence,
  });

  const needsOfflineSetupHint = shouldShowOfflineSetupRequired({
    session: input.session,
    accountSetupResolved: input.accountSetupResolved,
    needsOnboarding: needsEntryScreen,
    requiresOwnerOnboarding: input.requiresOwnerOnboarding,
    offlineBootstrapEvidence: input.offlineBootstrapEvidence,
    hasBusinessProfile: input.hasBusinessProfile,
  });

  const waitingOperationalGate =
    input.session &&
    input.accountSetupResolved &&
    !needsEntryScreen &&
    !needsOfflineSetupHint &&
    !accountOperationalIncludingOffline;

  const bootstrapping =
    !input.initialAuthReady ||
    (input.session && !input.accountSetupResolved) ||
    waitingOfflineBootstrapEvidence ||
    (input.session && !needsEntryScreen && !input.initialBranchSelectionReady) ||
    (input.session && !needsEntryScreen && !input.employeeAccessReady) ||
    waitingOperationalGate;

  return {
    needsOnboarding,
    needsPendingInvitation,
    needsAwaitingInvitation,
    needsOfflineSetupHint,
    accountOperational,
    accountOperationalIncludingOffline,
    bootstrapping,
  };
}
